import { FilterResponse } from '@/components/HandlerDashboardDrawer/components/Filter/types'
import { ChatModel, ChatsInformationModel } from '@/models/chat.model'
import { ConfiguringChatModel } from '@/models/configuringChat.model'
import { MessageModel } from '@/models/message.model'
import { api } from '@/services/api'

type GetChatsByStatusParams = {
  ownerId: string
  campaignIds: string[]
  status: string[]
  operatorId?: string
  departmentId?: string
  isChatsWithUnreadedMessages?: boolean
  queryLimit?: number
  chatFilters?: ChatFilters
}

export type ChatFilters = {
  labelIds?: string[]
  isUra?: boolean
  isFunnel?: boolean
  period?: Period
  requesterNumber?: string
}

type Period = {
  start: number
  end: number
}

export type GetChatsByStatusResponse = {
  chats: ChatModel[]
}

export async function getChatsByStatus(params: GetChatsByStatusParams) {
  const response = await api.post<GetChatsByStatusResponse>('/mongo/chats/get-chats-by-status', params)
  return response.data
}

export type CreateChatResponse = {
  chat: ChatModel | null
}

export async function create(chat: ChatModel) {
  const response = await api.post<CreateChatResponse>('/mongo/chats', {
    chat
  })
  return response.data.chat
}

export async function getTickets(chat: Partial<ChatModel>) {
  const response = await api.get<{ tickets: NonNullable<ChatModel['lastTickets']> }>(`/mongo/chats/${chat.id}/tickets`)
  return response.data
}

export async function update(chatId: string, updatedData: Partial<ChatModel>) {
  await api.post('/mongo/chats/update', {
    id: chatId,
    dataToUpdate: updatedData
  })
}

export async function setControlChange(chat: ChatModel) {
  await api.post('/chats/set-control-change', {
    chat
  })
}

type FetchMoreParams = {
  ownerId: string
  campaignIds: string[]
  status: string[]
  departmentId: string
  startAfter: number
  operatorId?: string
  limit?: number
}

export type FetchMoreResponse = {
  chats: ChatModel[]
  hasMore: boolean
}

export async function fetchMore(params: FetchMoreParams) {
  const response = await api.post<FetchMoreResponse>('/mongo/chats/fetch-more', params)
  return response.data
}

export async function read(chatId: string) {
  await api.get(`/mongo/chats/${chatId}/read`)
}

type FetchMessagesParams = {
  chatId: string
  ownerId: string
  queryLimit?: number
  startAfter?: number
}

export type FetchMessagesResponse = {
  messages: MessageModel[]
  hasMore: boolean
}

export async function fetchMessages(params: FetchMessagesParams) {
  let queryParams = `?ownerId=${params.ownerId}&chatId=${params.chatId}`
  if (params?.queryLimit) {
    queryParams += `&queryLimit=${params.queryLimit}`
  }
  if (params?.startAfter) {
    queryParams += `&startAfter=${params.startAfter}`
  }
  const response = await api.get<FetchMessagesResponse>(`/chats/messages${queryParams}`)
  return response.data
}

type FetchChatConfigParams = {
  campaignId: string
  ownerId: string
}

export type FetchChatConfigResponse = {
  chatConfigs: ConfiguringChatModel
}

export async function fetchChatConfig(params: FetchChatConfigParams) {
  const response = await api.get<FetchChatConfigResponse>(`/chats/chat-configs/${params.ownerId}/${params.campaignId}`)
  return response.data
}

type FetchChatsInformationParams = {
  ownerId: string
  campaignIds: string[]
  departmentId?: string
  operatorId?: string
  filters?: FilterResponse
}

export type FetchChatsInformationResponse = {
  chatsInformation: ChatsInformationModel
}

export async function fetchChatsInformation(params: FetchChatsInformationParams) {
  const response = await api.get<FetchChatsInformationResponse>('/mongo/chats/fetch-chats-information', {
    params
  })
  return response.data
}

export type GetChatByIdResponse = {
  chat: ChatModel
}

export async function getChatById(chatId: string, ownerId: string) {
  const response = await api.get<GetChatByIdResponse>(`/mongo/chats/${chatId}`, { params: { ownerId } })
  return response.data
}
